import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../components/molecules/Layout';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import { IContactForm } from '../types/contactForm.types';
import { contactSchema } from '../schemas/contact.schema';
import { submitContactForm } from '../services/contact';
import useToast from '../hooks/useToast';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

interface ContactFormProps {
  executeRecaptcha?: (action: string) => Promise<string>;
}

const ContactForm = ({ executeRecaptcha }: ContactFormProps) => {
  const [message, setMessage] = useState('');
  const toast = useToast();
  const honeypotRef = useRef<HTMLInputElement>(null);

  const { handleSubmit, register, reset } = useForm<IContactForm>({
    resolver: yupResolver(contactSchema),
  });

  const onSubmit = useCallback(
    async (values: IContactForm) => {
      // Honeypot check: if the hidden field has a value, silently "succeed"
      if (honeypotRef.current && honeypotRef.current.value) {
        toast.addToast({
          type: 'success',
          message: 'Message sent 📤!',
        });
        return;
      }

      // Get reCAPTCHA token
      if (executeRecaptcha) {
        const recaptchaToken = await executeRecaptcha('contact_form');
        values.recaptchaToken = recaptchaToken;
      }

      submitContactForm(values)
        .then(response => {
          if (response === 'success') {
            toast.addToast({
              type: 'success',
              message: 'Message sent 📤!',
            });
            reset();
          } else {
            toast.addToast({
              type: 'error',
              message: 'There was an error, try again later.',
            });
          }
        })
        .catch(error => {
          toast.addToast({
            type: 'error',
            message: 'There was an error, try again later.',
          });
        });
    },
    [executeRecaptcha, toast, reset]
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="support_form"
      method="post"
      action=""
    >
      <input type="hidden" name="User" value="0" />

      {/* Honeypot field - hidden from real users, bots will fill it in */}
      <div aria-hidden="true" className="honeypot-field">

        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          ref={honeypotRef}
        />
      </div>

      <div className="form-group">
        <label className="control-label">Name</label>
        <input className="form-control" type="text" {...register('name')} />
      </div>

      <div className="form-group">
        <label className="control-label">Email</label>
        <input className="form-control" type="email" {...register('email')} />
      </div>

      <div className="form-group">
        <label className="control-label">Subject</label>
        <select className="form-control" {...register('subject')}>
          <option>General Contact</option>
          <option>Account Help</option>
        </select>
      </div>

      <p id="bug_report_message" className="field_message hidden">
        Got a problem with the website? For starters, try{' '}
        <a href="logout">logging out</a> and then logging back in again.
      </p>

      <div className="form-group">
        <label className="control-label">Message</label>
        <textarea
          required
          className="form-control"
          placeholder="How can we help?"
          {...register('message')}
        ></textarea>
      </div>

      <div className="button_outer">
        <button type="submit" className="btn btn-primary btn--has-arrow">Send Message</button>
      </div>
      <div className="button_outer">{message}</div>
    </form>
  );
};

const RecaptchaContactForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  return <ContactForm executeRecaptcha={executeRecaptcha ?? undefined} />;
};

const Contact = () => {
  useEffect(() => {
    document.title =
      'Contact - LogoLounge - Logo Design Inspiration & Competition - Work';
  }, []);

  return (
    <Layout className={clsx('support has_left_background')} showRibbon={true}>
      <main id="content">
        <figure></figure>
        <article
          className="article"
          itemScope={false}
          itemType="http://schema.org/Article"
        >
          <div className="content_outer">
            <h1>Contact</h1>

            {RECAPTCHA_SITE_KEY ? (
              <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
                <RecaptchaContactForm />
              </GoogleReCaptchaProvider>
            ) : (
              <ContactForm />
            )}
          </div>
        </article>
      </main>
    </Layout>
  );
};

export default Contact;
